import { useContext, useEffect, useMemo, useState } from "react";
import Button from "../../components/Button";
import { AppContext } from "../../context/app.context";
import { ScheduleContext } from "../../context/schedule.context";
import { ModelCxContext } from "../../context/modelCx.context";
import {
  FILTERS_CLASSIFICATION_ARRAY,
  FILTERS_CREW_ARRAY,
  FILTERS_CREW_SURFACE_ARRAY,
} from "../../utility/keys";
import Checkbox from "../../components/Checkbox";
import { shouldHideInstallerRow } from "../../utility/installerHasFullWeekOff";

// - - - - - - - - - -
// Global toolbar for the Optimizer screen (#105 phase 3).
// Always rendered once at the top of the Optimizer, above all grids and
// the RTS sidebar. Holds controls that apply to ALL grids:
//   - Show 'OFF' checkbox + hidden-count badge
//   - Location filter + meta button
//   - Capacity View toggle
//   - Updated timestamp + polling indicators
//   - Filter section toggle + filter section (Crews / Surfaces / Jobs)
//
// The per-grid week-nav controls (Today / WeekPicker) live in
// <PerGridToolbar> inside each grid's header.
//
// Cut from ScheduleHeader.js, which the legacy Scheduler tab still uses
// unchanged. The duplication is intentional — we don't want to break the
// legacy tab while the Optimizer evolves.

const GlobalToolbar = ({
  onAddGrid,
  // Phase 6 — Time Machine repurposes this slot as a single-grid ↔ Diff
  // Pair toggle. The button stays visually identical (same icon position)
  // but the semantics flip in time-travel. Optional props let the caller
  // override the title/aria-label and add an active-state highlight.
  //
  // addGridIsToggle (PR #118 follow-up): when true, the button is acting
  // as a two-state toggle (Phase 6 Diff Pair) and `aria-pressed` is
  // emitted as an explicit boolean for assistive tech. When false (default,
  // outside time-travel), the button is a single-action add and
  // `aria-pressed` is omitted entirely (correct ARIA semantics — pressed
  // state only applies to toggles).
  addGridTitle = "Add a new schedule grid to the right",
  addGridAriaLabel = "Add schedule grid",
  addGridActive = false,
  addGridIsToggle = false,
} = {}) => {
  const [hideFilter, setHideFilter] = useState(true);
  const [latestChanged, setLatestChanged] = useState("<1 min ago");
  const [intervalDelay, setIntervalDelay] = useState(1000 * 5); // 5 sec
  const {
    locationFilter,
    locationArray,
    setLocationFilter,
    weekDatesArray,
    countsToggle,
    setCountsToggle,
    currentFilters,
    onCrewFiltersChanged,
    onCrewSurfaceFiltersChanged,
    onClassificationFiltersChanged,
    setOpenLocationMetaModal,
    latestChangeTS,
    dataFormatting_payrollFields,
    isTimeTraveling,
  } = useContext(AppContext);

  const {
    schedulePreparing,
    setScheduleNeedsModel,
    scheduleArray,
    showOffInstallers,
    setShowOffInstallers,
  } = useContext(ScheduleContext);

  const {
    shouldPollProjects,
    projectsFetching,
    shouldPollAttributes,
    attributesPollingFetching,
    awsgwCandidatesResult,
    awsgwCandidatesResultLocation,
  } = useContext(ModelCxContext);

  // Hidden-count badge: depends on weekDatesArray. For Phase 3 (single grid,
  // AppContext is still source of truth) this is correct. Phase 4b will need
  // to decide multi-grid semantics (sum across grids? leftmost grid only?).
  const hiddenOffCount = useMemo(() => {
    if (showOffInstallers) return 0;
    if (awsgwCandidatesResultLocation !== locationFilter) return 0;
    if (!Array.isArray(awsgwCandidatesResult)) return 0;
    return awsgwCandidatesResult.reduce((count, candidate) => {
      const payrollNo = dataFormatting_payrollFields(candidate.payrollNo);
      return (
        count +
        (shouldHideInstallerRow(
          candidate,
          payrollNo,
          weekDatesArray,
          scheduleArray,
          locationFilter
        )
          ? 1
          : 0)
      );
    }, 0);
  }, [
    showOffInstallers,
    awsgwCandidatesResult,
    awsgwCandidatesResultLocation,
    weekDatesArray,
    scheduleArray,
    locationFilter,
    dataFormatting_payrollFields,
  ]);

  // - - - - - - - - - -

  const onLocationChange = (event) => {
    const newLocation = event.target.value;
    console.log("GlobalToolbar onLocationChange: ", newLocation);
    setLocationFilter(newLocation);
    setScheduleNeedsModel(true);
  };

  const onCapacityViewChange = () => {
    console.log("GlobalToolbar onCapacityClick, countsToggle: ", !countsToggle);
    setCountsToggle(!countsToggle);
  };

  const onLocationMetaClick = () => {
    console.log("GlobalToolbar onLocationMetaClick...");
    setOpenLocationMetaModal({ show: true, currentLocation: locationFilter });
  };

  // - - - - - - - - - -
  // "Updated: ..." stale-timestamp ticker, mirrors ScheduleHeader.

  useEffect(() => {
    const intervalId = setInterval(() => {
      let newLatestChanged = "";
      const diffSec = ((new Date() - latestChangeTS) / 1000).toFixed();

      if (diffSec < 60) {
        newLatestChanged = "<1 min ago";
        if (intervalDelay !== 1000 * 5) setIntervalDelay(1000 * 5); // 5 sec
      } else if (diffSec < 3600) {
        const diffMin = (diffSec / 60).toFixed();
        newLatestChanged = `${diffMin} min ago`;
        if (intervalDelay !== 1000 * 30) setIntervalDelay(1000 * 30); // 30 sec
      } else if (diffSec < 86400) {
        const diffHour = (diffSec / 3600).toFixed();
        newLatestChanged = `${diffHour} hr ago`;
        if (intervalDelay !== 1000 * 60 * 5) setIntervalDelay(1000 * 60 * 5); // 5 min
      } else {
        const diffDay = (diffSec / 86400).toFixed();
        newLatestChanged = `${diffDay} day ago`;
        if (intervalDelay !== 1000 * 60 * 60) setIntervalDelay(1000 * 60 * 60); // 1 hr
      }

      setLatestChanged(newLatestChanged);
    }, intervalDelay);

    return () => clearInterval(intervalId);
  }, [latestChangeTS, intervalDelay]);

  useEffect(() => {
    setIntervalDelay(0);
  }, [latestChangeTS]);

  return (
    <>
      <section className="flex gap-12 text-sm font-medium items-center justify-center flex-wrap">
        <div className="flex items-center gap-2">
          <Checkbox
            label="Show 'OFF' rows"
            value={showOffInstallers}
            onChange={() => setShowOffInstallers(!showOffInstallers)}
            disabled={schedulePreparing}
          />
          {!showOffInstallers && hiddenOffCount > 0 && (
            <span className="text-sm text-skin-primary whitespace-nowrap">
              ({hiddenOffCount} hidden)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="location" className="self-center">
            Location
          </label>
          <button onClick={() => onLocationMetaClick()}>ℹ️</button>
          <select
            id="location"
            className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
            onChange={onLocationChange}
            disabled={schedulePreparing}
            value={locationFilter}
          >
            {locationArray.map((location, index) => (
              <option value={location} key={index}>
                {location}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="self-center">Capacity View</span>
          <div className="flex">
            <Button
              onClick={() => onCapacityViewChange()}
              color={!countsToggle && "inactive"}
            >
              Days
            </Button>
            <Button
              onClick={() => onCapacityViewChange()}
              color={countsToggle && "inactive"}
            >
              Jobs
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Time Machine #114: hide "Updated:" indicator while time-traveling.
              Polling is paused; the displayed data is historical, not live. */}
          {!isTimeTraveling && <span>Updated: {latestChanged}</span>}

          {/* Polling Indicators - small spinners when polling is active */}
          <div
            className="flex items-center gap-1"
            style={{ minWidth: "36px", height: "16px" }}
          >
          {shouldPollProjects && projectsFetching && (
            <div
              title="Polling projects..."
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid rgba(34, 197, 94, 0.3)",
                borderTop: "2px solid rgb(34, 197, 94)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          )}
          {shouldPollAttributes && attributesPollingFetching && (
            <div
              title="Polling attributes..."
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid rgba(0, 206, 209, 0.3)",
                borderTop: "2px solid rgb(0, 206, 209)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          )}
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
        </div>

        {onAddGrid && (
          <button
            onClick={onAddGrid}
            title={addGridTitle}
            aria-label={addGridAriaLabel}
            aria-pressed={addGridIsToggle ? addGridActive : undefined}
            data-tutorial-target="add-grid-toggle"
            className={`hover:opacity-75 rounded p-0.5 ${
              addGridActive ? "bg-yellow" : ""
            }`}
          >
            {/* Inlined from add-column-right-outline-rounded.svg so the
                fill follows the theme via fill-skin-base (matches the
                filter-toggle eye icon next to it). The original SVG file
                had a baked-in gray color when loaded via <img>. */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="fill-skin-base"
            >
              <path d="M10 5H4v14h6zM4 21q-.825 0-1.412-.587T2 19V5q0-.825.588-1.412T4 3h14q.825 0 1.413.588T20 5v1q0 .425-.288.713T19 7t-.712-.288T18 6V5h-6v14h6v-1q0-.425.288-.712T19 17t.713.288T20 18v1q0 .825-.587 1.413T18 21zm14.288-6.288Q18 14.425 18 14v-1h-1q-.425 0-.712-.288T16 12t.288-.712T17 11h1v-1q0-.425.288-.712T19 9t.713.288T20 10v1h1q.425 0 .713.288T22 12t-.288.713T21 13h-1v1q0 .425-.288.713T19 15t-.712-.288M10 12h2zm0 0" />
            </svg>
          </button>
        )}

        <button onClick={() => setHideFilter(!hideFilter)}>
          {hideFilter ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="fill-skin-base"
            >
              <g clipPath="url(#clip0_global_filter)">
                <path d="M6.99999 6H17L11.99 12.3L6.99999 6ZM4.24999 5.61C6.26999 8.2 9.99999 13 9.99999 13V19C9.99999 19.55 10.45 20 11 20H13C13.55 20 14 19.55 14 19V13C14 13 17.72 8.2 19.74 5.61C20.25 4.95 19.78 4 18.95 4H5.03999C4.20999 4 3.73999 4.95 4.24999 5.61Z" />
              </g>
              <defs>
                <clipPath id="clip0_global_filter">
                  <rect width="24" height="24" />
                </clipPath>
              </defs>
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              className="fill-skin-base"
            >
              <mask
                id="mask0_global_filter"
                style={{ maskType: "alpha" }}
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="24"
                height="24"
              >
                <rect width="24" height="24" />
              </mask>
              <g mask="url(#mask0_global_filter)">
                <path d="M14.7999 11.975L13.3749 10.55L16.9499 6H8.8249L6.8249 4H18.9999C19.4166 4 19.7166 4.18334 19.8999 4.55C20.0832 4.91667 20.0499 5.26667 19.7999 5.6L14.7999 11.975ZM19.7749 22.6L13.9999 16.825V19C13.9999 19.2833 13.9041 19.5208 13.7124 19.7125C13.5207 19.9042 13.2832 20 12.9999 20H10.9999C10.7166 20 10.4791 19.9042 10.2874 19.7125C10.0957 19.5208 9.9999 19.2833 9.9999 19V12.825L1.3999 4.225L2.7999 2.8L21.1999 21.2L19.7749 22.6Z" />
              </g>
            </svg>
          )}
        </button>
      </section>
      {hideFilter ? (
        ""
      ) : (
        <section className="mt-4">
          <div className="flex">
            <span className="w-14 text-sm font-medium">Crews</span>
            {FILTERS_CREW_SURFACE_ARRAY.map(({ name }, index) => (
              <div className="flex-1" key={index}>
                <Checkbox
                  label={name}
                  disabled={schedulePreparing}
                  value={currentFilters.crewSurfaceFilters[index]}
                  onChange={() => onCrewSurfaceFiltersChanged(index)}
                />
              </div>
            ))}
            {FILTERS_CREW_ARRAY.map(({ name }, index) => (
              <div className="flex-1" key={index}>
                <Checkbox
                  label={name}
                  disabled={schedulePreparing}
                  value={currentFilters.crewFilters[index]}
                  onChange={() => onCrewFiltersChanged(index)}
                />
              </div>
            ))}
            <span className="flex-1"></span>
          </div>
          <div className="flex">
            <span className="w-14 text-sm font-medium">Jobs</span>
            {FILTERS_CLASSIFICATION_ARRAY.map(({ name }, index) => (
              <div className="flex-1 overflow-hidden" key={index}>
                <Checkbox
                  label={name}
                  disabled={schedulePreparing}
                  value={currentFilters.classificationFilters[index]}
                  onChange={() => onClassificationFiltersChanged(index)}
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export default GlobalToolbar;
